// Warm-up while a call rings (CU-869fmcc9r): Create lead and Script must open within
// 500 ms of a press on the pill, so what they wait for is fetched as soon as the call
// shows, and again when it moves on (answered, ended):
//   - Create lead: the server's pre-fill for the call (callerActions.api keeps it for the press);
//   - Script, known caller: the lead and the outbound script for their treatment (kept here);
//   - Script, unknown caller: the practice's inbound scripts with their sections (redux).
// The script and Create lead code is loaded too. Everything is best effort: a press that
// finds nothing ready simply fetches as before.
import { callApi } from '../../utils/apiCall';
import { dispatch } from '../../redux/store';
import { callScriptSectionUrl, inboundScriptsUrl, setInboundScriptsData } from '../../redux/slices/callScript';
import { prefetchLeadPrefill } from '../../pages/dashboard/callLogs/caller/callerActions.api';
import { normaliseCall } from '../callFlow/callFlowModel';
import { leadDetailUrl, latestTreatmentOf } from '../leadFollowUp/leadFollowUpModel';

const FRESH_MS = 2 * 60 * 1000;
const leads = new Map();
const inbound = new Map();

const fresh = (entry) => Boolean(entry) && Date.now() - entry.at < FRESH_MS;
const leadKey = (practiceId, leadId) => `${practiceId}:${leadId}`;

const okData = (res) => (res?.ok && res.data && typeof res.data === 'object' ? res.data : null);

// The lead and the outbound scripts for their latest treatment: { lead, scripts } (either null).
const fetchLeadScript = async (practiceId, leadId) => {
    const lead = okData(await callApi(leadDetailUrl(practiceId, leadId), 'GET', null, false, { suppressServerErrorDispatch: true }));
    if (!lead) return { lead: null, scripts: null };
    const treatment = latestTreatmentOf(lead)?.treatment || '';
    const scripts = okData(await callApi(
        callScriptSectionUrl({ practiceId, treatment, isTemplate: false, category: 'OUTBOUND' }),
        'GET', null, false, { suppressServerErrorDispatch: true },
    ));
    return { lead, scripts: Array.isArray(scripts?.results) ? scripts : null };
};

export function prefetchLeadScript(practiceId, leadId) {
    if (!practiceId || !leadId) return;
    const key = leadKey(practiceId, leadId);
    if (fresh(leads.get(key))) return;
    leads.set(key, { at: Date.now(), answer: fetchLeadScript(practiceId, leadId).catch(() => ({ lead: null, scripts: null })) });
}

// The prefetched { lead, scripts } promise for this lead, or null when there is none (or it's old).
export function takeLeadScript(practiceId, leadId) {
    const entry = leads.get(leadKey(practiceId, leadId));
    return fresh(entry) ? entry.answer : null;
}

const loadScreens = () => {
    // The script screens are split into their own chunks: fetch them now, not on the press.
    import('./PillScript').catch(() => null);
    import('../leadFollowUp/LeadFollowUpLauncher').catch(() => null);
};

// Everything a press on this call's pill may need. `refresh`: the call moved on, ask the
// pre-fill again (its first note names the call's times). Called again every minute while
// the call shows: anything older than FRESH_MS is fetched again, so a press late in a long
// call still finds it ready.
export function prefetchForCall(practiceId, call, { refresh = false } = {}) {
    if (!practiceId || !call) return;
    loadScreens();
    const flowCall = normaliseCall(call);
    if (flowCall?.row && !call.lead?.id && !call.callerIdWithheld) prefetchLeadPrefill(practiceId, flowCall.row, { refresh });
    if (call.lead?.id) prefetchLeadScript(practiceId, call.lead.id);
    // The inbound list for everyone: a known caller whose treatment has no outbound script
    // gets the inbound script too (PillScript).
    if (!fresh(inbound.get(String(practiceId)))) {
        inbound.set(String(practiceId), { at: Date.now() });
        // The same list the inbound script loads on open, with sections, so it can show at once.
        callApi(inboundScriptsUrl(practiceId), 'GET', null, false, { suppressServerErrorDispatch: true })
            .then((res) => { if (res?.status === 200 && res.data) dispatch(setInboundScriptsData(res.data)); })
            .catch(() => null);
    }
}

// Tests only.
export const resetCallPrefetch = () => { leads.clear(); inbound.clear(); };
